import {ClickAwayListener, css, InputAdornment, Popper, useMediaQuery, useTheme} from '@mui/material';
import {DateUtils, DlMuiCalendarIcon, DlMuiDatePicker, DlMuiTextField, utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {differenceInMonths, isValid} from 'date-fns';
import {isNil} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useRef, useState, MouseEvent, useLayoutEffect} from 'react';
import {Placement} from 'react-bootstrap/esm/types';
import DatePicker from 'react-datepicker';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {DateRangeFilter, DynamicFilter, Filter, FILTER_TYPE_DATE_MONTH_RANGE, MonthRangeFilter, ShortcutEnum} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';

import {STATIC_EMPTY_ARRAY} from './FilterNodeContainer';
import {FilterNodeTypeDateMonthRangeCalendarHeader} from './FilterNodeTypeDateMonthRangeCalendarHeader';
import {dateRangeGetStyles, DlMuiDatePickerLayout, DlMuiPickersDay} from './FilterNodeTypeDateRange';
import styles from './FilterNodeTypeDateRange.less';
import {FilterNodeTypeWrapper} from './FilterNodeTypeWrapper';
import {FilterSubmitButton} from './FilterSubmitButton';
import {FilterNodeRangeShortcuts} from './FilterNodeRangeShortcuts';
import {FilterNodeRangeActionButtons} from './FilterNodeRangeActionButtons';
import {Shortcut} from '../../../lib/filtering/shortcut_types';
import {getShortcuts} from '../../../lib/filtering/shortcuts';

const I18N = defineMessages({
  FROM: {
    id: 'filter.date.from',
    defaultMessage: 'From',
    description: 'label for start date in date picker input',
  },
  TO: {
    id: 'filter.date.to',
    defaultMessage: 'To',
    description: 'label for end date in date picker input',
  },
  END_PLACEHOLDER: {
    id: 'filter.date.end.placeholder',
    defaultMessage: 'end date',
    description: 'placeholder for end date in date picker input',
  },
  START_PLACEHOLDER: {
    id: 'filter.date.start.placeholder',
    defaultMessage: 'start date',
    description: 'placeholder for start date in date picker input',
  },
  TOTAL_TEXT: {
    id: 'filter.month.totalText',
    defaultMessage: 'Total interval: {dateDifference} months',
    description: 'summary text for total interval',
  },
});

type Props = {
  node: FilterNodeModel;
  changeFilter: (newFilter: MonthRangeFilter) => void;
  inputRef: (input: any) => void;
  isOpened: boolean;
  removeFilter: (newFilter: MonthRangeFilter) => void;
  selectedFilter: MonthRangeFilter;
  setFocus: () => void;
  toggleOpened: () => void;
  isMui?: boolean;
  isDefaultValue?: boolean;
  placement?: Placement;
  fullWidth?: boolean;
  isEmpty?: boolean;
  isRemoveFilterButtonHidden?: boolean;
  shortcut?: ShortcutEnum | null;
  showShortcuts?: boolean;
  isResetFiltersButtonDisabled?: boolean;
};
type State = {
  start: Date | null;
  end: Date | null;
  prevStart: Date | null;
  prevEnd: Date | null;
  shortcut?: ShortcutEnum | null;
};
const FilterNodeTypeDateMonthRange: FC<Props> = (props) => {
  const {
    node,
    changeFilter: _changeFilter,
    inputRef,
    isOpened = false,
    removeFilter,
    selectedFilter,
    setFocus,
    toggleOpened,
    isMui,
    isDefaultValue,
    placement = 'bottom-start',
    fullWidth,
    isEmpty = false,
    isRemoveFilterButtonHidden = false,
    showShortcuts,
    isResetFiltersButtonDisabled,
  } = props;
  // state
  const state = useRef({
    start: new Date(),
    end: new Date(),
    prevStart: null,
    prevEnd: null,
    shortcut: ShortcutEnum.PREVIOUS_MONTH,
  } as State);
  const _rerender = useState(0)[1];
  const rerender = useCallback(() => _rerender((v) => v + 1), [_rerender]);
  const setState = useCallback(
    (newState: State) => {
      state.current = newState;
      rerender();
    },
    [rerender]
  );
  useEffect(() => {
    let startDate: Date | null = selectedFilter ? selectedFilter.getStartDate() : null;
    if (!isValid(startDate)) startDate = null;
    let endDate: Date | null = selectedFilter ? selectedFilter.getEndDate() : null;
    if (!isValid(endDate)) endDate = null;
    let shortcut: ShortcutEnum | null | undefined;
    if (selectedFilter && 'getShortcut' in selectedFilter) {
      shortcut = selectedFilter.getShortcut();
    }
    // A saved filter may carry a stale shortcut (e.g. a default PREVIOUS_MONTH preserved while
    // the user customized the dates). Drop the shortcut whenever it no longer matches the
    // loaded range so the picker does not highlight a preset that does not match the dates.
    if (shortcut && startDate && endDate) {
      const allShortcuts = getShortcuts(false, false);
      const matched = [...allShortcuts.monthlyShortcuts, ...allShortcuts.commonShortcuts].find((sc) => sc.label === shortcut);
      if (matched) {
        const expected = matched.getValue();
        const sameMonth = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
        if (!sameMonth(startDate, expected.start) || !sameMonth(endDate, expected.end)) {
          shortcut = null;
        }
      }
    }
    if (startDate !== state.current.prevStart || endDate !== state.current.prevEnd) {
      setState({
        ...state.current,
        start: startDate || new Date(),
        end: endDate || new Date(),
        prevStart: startDate ?? null,
        prevEnd: endDate ?? null,
        shortcut,
      });
    }
  }, [selectedFilter]); // eslint-disable-line
  const [suppressFocus, setSuppressFocus] = useState(false);
  const prevIsOpened = useRef(isOpened);
  useEffect(() => {
    if (isOpened !== prevIsOpened.current) {
      setSuppressFocus(!isOpened || prevIsOpened.current);
      prevIsOpened.current = isOpened;
    }
  }, [isOpened]);
  // callbacks
  const getFilter = useCallback(
    (value: {startDate: string; endDate: string; shortcut?: ShortcutEnum | null} | undefined | null) => {
      const filterType = FILTER_TYPE_DATE_MONTH_RANGE;
      const MonthRangeFilter = DynamicFilter.getFilterClass(filterType);
      return new MonthRangeFilter({
        node,
        type: filterType,
        property: node.getName(),
        value,
      });
    },
    [node]
  );
  const changeFilter = useCallback(
    (value: {startDate: string; endDate: string; shortcut?: ShortcutEnum | null}) => {
      _changeFilter(getFilter(value));
    },
    [_changeFilter, getFilter]
  );
  const isDisabled = useCallback(() => {
    const {end, start} = state.current;
    return isNil(start) || isNil(end) || end < start;
  }, []);
  const handleButtonSubmit = useCallback(() => {
    if (!isDisabled()) {
      changeFilter({
        startDate: DateUtils.toMonthFormat(state.current.start as Date),
        endDate: DateUtils.toMonthFormat(state.current.end as Date),
        shortcut: state.current.shortcut,
      });
    }
  }, [isDisabled, changeFilter]);
  const handleChangeStart = useCallback(
    (value: Date | null) => {
      setState({...state.current, start: value, shortcut: null});
      setSuppressFocus(true);
    },
    [state, setState]
  );
  const handleChangeEnd = useCallback(
    (value: Date | null) => {
      setState({...state.current, end: value, shortcut: null});
      setSuppressFocus(true);
    },
    [state, setState]
  );
  const handleShortcut = useCallback(
    (value?: ShortcutEnum | null) => {
      setState({...state.current, shortcut: value});
      setSuppressFocus(true);
      if (isMui) handleButtonSubmit();
    },
    [state, setState, isMui, handleButtonSubmit]
  );
  const getStartDate = useCallback(() => (isNil(state.current.start) ? null : new Date(state.current.start)), []);
  const getEndDate = useCallback(() => (isNil(state.current.end) ? null : new Date(state.current.end)), []);
  const getDateDifference = useCallback(() => {
    const {end, start} = state.current;
    // NOTE: (1.1.2018 - 1.1.2018) is 1 day
    const diff = !isNil(start) && !isNil(end) ? differenceInMonths(DateUtils.resetTime(end), DateUtils.resetTime(start)) + 1 : 0;
    return diff;
  }, []);
  const getMonthDifference = useCallback(() => {
    // NOTE: (1.1.2018 - 31.1.2018) is 1 month
    const {start, end} = state.current;
    if (!start || !end) return 1;
    const getMonthNumber = (date: Date) => date.getFullYear() * 12 + date.getMonth();
    return getMonthNumber(end) - getMonthNumber(start) + 1;
  }, []);

  const filter = getFilter(undefined);
  const startDate = getStartDate();
  const endDate = getEndDate();
  const dateDifference = getDateDifference();
  const monthDifference = getMonthDifference();
  const intl = useIntl();
  const locale = DateUtils.translateLocaleToDateFnsLocale(intl.locale);

  // mui only
  const today = DateUtils.today();
  const theme = useTheme();
  const muiStyles = dateRangeGetStyles({theme, params: {fullWidth}});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isPopperOpened = Boolean(anchorEl);
  const isDailyView = false;
  const enableFuture = false;

  // Resync local state from the last committed value when the popper closes,
  // so abandoned header-arrow navigation does not leak into the next open.
  const prevIsPopperOpened = useRef(isPopperOpened);
  useEffect(() => {
    if (prevIsPopperOpened.current && !isPopperOpened) {
      setState({
        ...state.current,
        start: state.current.prevStart || new Date(),
        end: state.current.prevEnd || new Date(),
      });
    }
    prevIsPopperOpened.current = isPopperOpened;
  }, [isPopperOpened, setState]);

  const currentShortcut = state.current.shortcut;
  const [activeShortcutButton, setActiveShortcutButton] = useState<ShortcutEnum | null | undefined>(currentShortcut);

  const handleShortcutSelect = useCallback(
    (dateRange: {start: Date; end: Date}, shortcut: ShortcutEnum | null) => {
      handleChangeStart(dateRange.start);
      handleChangeEnd(dateRange.end);
      handleShortcut(shortcut);
      handleButtonSubmit();
    },
    [handleChangeStart, handleChangeEnd, handleShortcut, handleButtonSubmit]
  );

  const shortcuts = useMemo(() => getShortcuts(enableFuture, isDailyView), [enableFuture, isDailyView]);

  useEffect(() => {
    if (activeShortcutButton === currentShortcut) return;
    setActiveShortcutButton(currentShortcut);

    const matchedShortcut = Object.values(shortcuts)
      .flat()
      .find((sc: Shortcut) => sc.label === currentShortcut);

    if (currentShortcut && matchedShortcut) {
      handleShortcutSelect(matchedShortcut?.getValue(), matchedShortcut?.label);
    }
  }, [activeShortcutButton, currentShortcut, shortcuts, handleShortcutSelect]);

  //TODO: PDH-2772
  /*const stripTimeFromDate = (date: Date | null) => {
    if (!date) return;
    return new Date(date?.getFullYear(), date?.getMonth(), date?.getDate());
  };

  const handleSelectMatchedShortcut = (newStartDate: Date | null, newEndDate: Date | null) => {
    const start = newStartDate ?? startDate;
    const end = newEndDate ?? endDate;

    if (!end || !start) return;

    const formattedEnd = new Date(end?.getFullYear(), end?.getMonth() + 1, 0);

    const selectedStart = stripTimeFromDate(start)?.getTime();
    const selectedEnd = stripTimeFromDate(formattedEnd)?.getTime();

    const matchedShortcut = [...shortcuts.monthlyShortcuts, ...shortcuts.commonShortcuts].find((shortcut) => {
      const {start, end} = shortcut.getValue();

      return stripTimeFromDate(start)?.getTime() === selectedStart && stripTimeFromDate(end)?.getTime() === selectedEnd;
    });

    if (matchedShortcut) {
      handleChangeStart(matchedShortcut.getValue().start);
      handleChangeEnd(matchedShortcut.getValue().end);
      handleShortcut(matchedShortcut.label);
      setActiveShortcutButton(matchedShortcut.label ?? null);
      handleButtonSubmit();
    }
  };*/

  const mouseEventClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(isPopperOpened ? null : event.currentTarget);
    },
    [isPopperOpened]
  );
  const handleDatePickerClose = () => {
    if (isPopperOpened) {
      setAnchorEl(null);
    }
  };
  // Apply commits current state (incl. value navigated via header arrows) and closes.
  // Cancel paths (ClickAway, Esc) keep using handleDatePickerClose so the unconfirmed
  // navigation is reverted by the popper-close reset effect.
  const handleDatePickerApply = () => {
    handleButtonSubmit();
    if (isPopperOpened) {
      setAnchorEl(null);
    }
  };
  const handleDatePickerClear = () => {
    if (isPopperOpened) {
      setAnchorEl(null);
    }
    removeFilter(selectedFilter);
  };

  const normalizedDates = useMemo(() => {
    const minPickerDate = new Date(1900, 0, 1);
    const maxPickerDate = today;

    const clampDate = (date: Date | null): Date | null => {
      if (!date) return null;
      if (date < minPickerDate) return minPickerDate;
      if (date > maxPickerDate) return maxPickerDate;
      return date;
    };

    return {
      startDate: clampDate(startDate),
      endDate: clampDate(endDate),
    };
  }, [startDate, endDate, today]);

  useLayoutEffect(() => {
    if (!isPopperOpened) return;

    const startMonth = normalizedDates.startDate
      ? new Date(normalizedDates.startDate.getFullYear(), normalizedDates.startDate.getMonth(), 1).getTime()
      : null;
    const endMonth = normalizedDates.endDate
      ? new Date(normalizedDates.endDate.getFullYear(), normalizedDates.endDate.getMonth(), 1).getTime()
      : null;

    if (!startMonth || !endMonth) return;

    const allMonthCalendars = document.querySelectorAll('.MuiMonthCalendar-root');
    if (allMonthCalendars.length < 2) return;

    const startCalendar = allMonthCalendars[0];
    const endCalendar = allMonthCalendars[1];

    const applyStylesToCalendar = (calendar: Element | null, isStartCalendar: boolean) => {
      if (!calendar) return;

      const dateCalendar = calendar.closest('.MuiDateCalendar-root');
      const yearSelect = dateCalendar?.querySelector('.MuiSelect-select');

      const yearText = yearSelect?.textContent?.trim() || '';
      const year = parseInt(yearText, 10);

      if (Number.isNaN(year)) return;

      const monthButtons = calendar.querySelectorAll('.MuiPickersMonth-root');

      monthButtons.forEach((button, index) => {
        const htmlButton = button as HTMLElement;
        const monthDate = new Date(year, index, 1).getTime();

        const isInRange = monthDate >= startMonth && monthDate <= endMonth;
        const isStart = monthDate === startMonth;
        const isEnd = monthDate === endMonth;

        htmlButton.removeAttribute('data-in-range');
        htmlButton.removeAttribute('data-range-start');
        htmlButton.removeAttribute('data-range-end');
        htmlButton.removeAttribute('data-year-end');
        htmlButton.removeAttribute('data-year-start');

        if (isInRange) {
          htmlButton.setAttribute('data-in-range', 'true');

          if (isStart) {
            htmlButton.setAttribute('data-range-start', 'true');
          }

          if (isEnd) {
            htmlButton.setAttribute('data-range-end', 'true');
          }

          if (index === 11 && isStartCalendar && isStart) {
            const nextYearFirstMonth = new Date(year + 1, 0, 1).getTime();
            if (nextYearFirstMonth >= startMonth && nextYearFirstMonth <= endMonth) {
              htmlButton.setAttribute('data-year-start', 'true');
            }
          }

          if (index === 0 && !isStartCalendar && isEnd) {
            const prevYearLastMonth = new Date(year - 1, 11, 1).getTime();
            if (prevYearLastMonth >= startMonth && prevYearLastMonth <= endMonth) {
              htmlButton.setAttribute('data-year-end', 'true');
            }
          }
        }
      });
    };

    applyStylesToCalendar(startCalendar, true);
    applyStylesToCalendar(endCalendar, false);
  }, [isPopperOpened, normalizedDates.startDate, normalizedDates.endDate]);

  const formattedDateRange = useMemo(() => {
    const formattedStart = DateUtils.getFormattedDate(normalizedDates.startDate, intl, true, true);
    const formattedEnd = DateUtils.getFormattedDate(normalizedDates.endDate, intl, true, true);
    return isEmpty ? '' : `${formattedStart} - ${formattedEnd}`;
  }, [startDate, endDate, intl, isEmpty]);

  const mediaMatches = useMediaQuery('(min-height:800px)');

  return isMui ? (
    <>
      <div css={muiStyles.textFieldWrapper}>
        <DlMuiTextField
          data-name={'filter-node-type-date-month-range--input'}
          label={node.getLabel() ?? ''}
          variant={'outlined'}
          onClick={mouseEventClick}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position='end'>
                <DlMuiCalendarIcon />
              </InputAdornment>
            ),
          }}
          value={formattedDateRange}
          inputCss={muiStyles.textField}
          isDefaultValue={isDefaultValue}
        />
      </div>
      {isPopperOpened ? (
        <ClickAwayListener
          mouseEvent='onMouseDown'
          onClickAway={(event) => {
            if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
              return;
            }
            handleDatePickerClose();
          }}
        >
          <Popper
            data-name={'dl-mui-popper--popper'}
            open={isPopperOpened}
            anchorEl={anchorEl}
            placement={mediaMatches ? placement : 'auto-start'}
            popperOptions={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: document.querySelector('[data-name="content-column"]'),
                    padding: {right: 16},
                  },
                },
              ],
            }}
            css={css`
              ${muiStyles.popper}
              ${muiStyles.monthPicker}
            `}
            disablePortal
          >
            <div
              css={muiStyles.dateRangeContainer}
              className={classNames({
                dateRangeContainerColumn: showShortcuts,
              })}
            >
              <div css={muiStyles.dateRangePickerWrapper}>
                {/* TODO: check this for displaying range - https://mui.com/x/react-date-pickers/date-calendar/#custom-day-rendering */}
                <DlMuiDatePicker
                  data-name={'filter-node-type-date-month-range--start'}
                  slotProps={{
                    actionBar: {hidden: true},
                  }}
                  slots={{
                    layout: (layoutProps) =>
                      DlMuiDatePickerLayout(layoutProps, {
                        styles: muiStyles,
                        dateFrom: true,
                        intl,
                        dateDifference: monthDifference,
                        isMonthInput: true,
                      }),
                    day: (dayProps) =>
                      DlMuiPickersDay({
                        ...dayProps,
                        endDate: normalizedDates.endDate,
                        startDate: normalizedDates.startDate,
                        isStartDate: true,
                      }),
                    calendarHeader: (headerProps) => (
                      <FilterNodeTypeDateMonthRangeCalendarHeader
                        value={normalizedDates.startDate}
                        onChange={handleChangeStart}
                        enableFuture={enableFuture}
                        {...headerProps}
                        boundMaxDate={normalizedDates.endDate}
                      />
                    ),
                  }}
                  value={normalizedDates.startDate}
                  // TODO: set endDate for startDate if it is lower
                  onChange={(value) => {
                    handleChangeStart(value);
                    handleButtonSubmit();
                    // handleSelectMatchedShortcut(value, null);
                  }}
                  maxDate={normalizedDates.endDate ?? today ?? undefined}
                  view='month'
                />
                <DlMuiDatePicker
                  data-name={'filter-node-type-date-month-range--end'}
                  value={normalizedDates.endDate}
                  minDate={normalizedDates.startDate ?? undefined}
                  maxDate={today}
                  onChange={(value) => {
                    handleChangeEnd(value);
                    handleButtonSubmit();
                    // handleSelectMatchedShortcut(null, value);
                  }}
                  slotProps={{
                    actionBar: {hidden: true},
                  }}
                  slots={{
                    layout: (customLayoutProps) =>
                      DlMuiDatePickerLayout(customLayoutProps, {
                        styles: muiStyles,
                        dateFrom: false,
                        intl,
                        isMonthInput: true,
                      }),
                    day: (dayProps) =>
                      DlMuiPickersDay({
                        ...dayProps,
                        endDate: normalizedDates.endDate,
                        startDate: normalizedDates.startDate,
                        isStartDate: false,
                      }),
                    calendarHeader: (headerProps) => (
                      <FilterNodeTypeDateMonthRangeCalendarHeader
                        value={normalizedDates.endDate}
                        onChange={handleChangeEnd}
                        enableFuture={enableFuture}
                        {...headerProps}
                        boundMinDate={normalizedDates.startDate}
                      />
                    ),
                  }}
                  view='month'
                />
              </div>
              <div
                css={muiStyles.barWrapper}
                className={classNames({
                  barWrapperColumn: showShortcuts,
                })}
              >
                {showShortcuts === true && (
                  <FilterNodeRangeShortcuts
                    isDailyView={isDailyView}
                    onShortcutSelect={handleShortcutSelect}
                    activeShortcutButton={activeShortcutButton ?? null}
                    setActiveShortcutButton={setActiveShortcutButton}
                    shortcuts={shortcuts}
                  />
                )}
                <FilterNodeRangeActionButtons
                  onDatePickerClear={handleDatePickerClear}
                  onDatePickerClose={handleDatePickerApply}
                  isResetFiltersButtonDisabled={isResetFiltersButtonDisabled}
                />
              </div>
            </div>
          </Popper>
        </ClickAwayListener>
      ) : null}
    </>
  ) : (
    <FilterNodeTypeWrapper
      filter={filter}
      isOpened={isOpened}
      node={node}
      removeFilter={removeFilter as unknown as (v: DateRangeFilter) => void}
      setFocus={setFocus}
      selectedFilter={selectedFilter as unknown as DateRangeFilter}
      suppressFocus={suppressFocus}
      toggleOpened={toggleOpened}
    >
      <div className={classNames('dlFilterNodeContentInput', styles.dlFilterNodeTypeDateRangeInputWrapper)}>
        <span>
          <FormattedMessage {...I18N.FROM} />
        </span>
        <div className={styles.dlInnerDataPickerWrapper} data-name={'monthFrom'}>
          <DatePicker
            className={styles.dlFilterNodeTypeDateRangeInput}
            onChange={handleChangeStart}
            dateFormat={DateUtils.MONTH_FORMAT}
            defaultValue={STATIC_EMPTY_ARRAY}
            disabledKeyboardNavigation
            dropdownMode={'select'}
            locale={locale}
            placeholderText={utils.TranslateUtils.translate(I18N.START_PLACEHOLDER, intl)}
            ref={(input) => inputRef(input ? input.input : null)}
            selected={normalizedDates.startDate}
            selectsEnd
            selectsStart
            showMonthYearPicker
            startDate={normalizedDates.startDate}
            endDate={normalizedDates.endDate}
            tabIndex={isOpened ? 0 : -1}
            utc
          />
        </div>
        <i className={classNames('far fa-calendar-alt', styles.dlFilterNodeTypeDateRangeIcon)} />
        <span>
          <FormattedMessage {...I18N.TO} />
        </span>
        <div className={styles.dlInnerDataPickerWrapper} data-name={'monthTo'}>
          <DatePicker
            className={styles.dlFilterNodeTypeDateRangeInput}
            onChange={handleChangeEnd}
            dateFormat={DateUtils.MONTH_FORMAT}
            defaultValue={STATIC_EMPTY_ARRAY}
            disabledKeyboardNavigation
            dropdownMode={'select'}
            locale={locale}
            placeholderText={utils.TranslateUtils.translate(I18N.END_PLACEHOLDER, intl)}
            selected={normalizedDates.endDate}
            selectsEnd
            selectsStart
            showMonthYearPicker
            startDate={normalizedDates.startDate}
            endDate={normalizedDates.endDate}
            tabIndex={isOpened ? 0 : -1}
            utc
          />
        </div>
        <i className={classNames('far fa-calendar-alt', styles.dlFilterNodeTypeDateRangeIcon)} />
      </div>
      <div className={styles.dlDayDifferenceWrapper}>
        <span
          className={classNames({
            [styles.dlDayDifferenceBad]: dateDifference < 0,
            [styles.dlDayDifferenceGood]: dateDifference >= 0,
          })}
        >
          <FormattedMessage {...I18N.TOTAL_TEXT} values={{dateDifference}} />
        </span>
        <FilterSubmitButton onClick={handleButtonSubmit} isDisabled={isDisabled()} tabIndex={isOpened ? 0 : -1} />
      </div>
    </FilterNodeTypeWrapper>
  );
};

FilterNodeTypeDateMonthRange.displayName = 'FilterNodeTypeDateMonthRange';

export default FilterNodeTypeDateMonthRange;
